import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { ArrowLeft, Mail, Lock, CheckCircle, Eye, EyeOff } from 'lucide-react';

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const resetToken = searchParams.get('token');
  
  const { requestPasswordReset, confirmPasswordReset } = useAuth();
  
  // Request reset state
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  
  // Confirm reset state
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [resetComplete, setResetComplete] = useState(false);

  const handleRequestReset = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    const result = await requestPasswordReset(email);
    
    if (result.success) {
      setEmailSent(true);
      toast.success('Check your email for reset instructions');
    } else {
      // Still show success to prevent email enumeration
      setEmailSent(true);
    }
    
    setLoading(false);
  };

  const handleConfirmReset = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    
    setLoading(true);
    
    const result = await confirmPasswordReset(resetToken, newPassword);
    
    if (result.success) {
      setResetComplete(true);
      toast.success('Password updated successfully');
    } else {
      toast.error(result.error);
    }
    
    setLoading(false);
  };

  // Reset Complete View
  if (resetComplete) {
    return (
      <div className="min-h-screen bg-[#F9F7F5] flex items-center justify-center px-4" data-testid="password-reset-complete">
        <div className="w-full max-w-md text-center">
          <div className="w-16 h-16 bg-[#6B8E6B]/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-8 h-8 text-[#6B8E6B]" />
          </div>
          <h1 className="font-heading text-2xl text-[#2C2420] mb-2">Password Updated</h1>
          <p className="text-[#5D524D] mb-6">
            Your password has been successfully reset.
          </p>
          <Button 
            onClick={() => navigate('/login')}
            className="caffeine-btn-primary"
            data-testid="go-to-login-btn"
          >
            Sign In
          </Button>
        </div>
      </div>
    );
  }

  // Confirm Reset View (when token is present)
  if (resetToken) {
    return (
      <div className="min-h-screen bg-[#F9F7F5] flex" data-testid="reset-password-page">
        <div className="w-full lg:w-1/2 flex flex-col px-8 py-6">
          <button 
            onClick={() => navigate('/login')}
            className="caffeine-btn-ghost inline-flex items-center gap-2 w-fit mb-8"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Login
          </button>

          <div className="flex-1 flex items-center justify-center">
            <div className="w-full max-w-md">
              <div className="flex items-center gap-2 mb-8">
                <div className="w-10 h-10 bg-[#4A3B32] rounded-md flex items-center justify-center">
                  <span className="text-white font-bold">Q1</span>
                </div>
                <span className="font-heading text-2xl text-[#2C2420]">QIndex One</span>
              </div>

              <h1 className="font-heading text-3xl text-[#2C2420] mb-2">
                Create New Password
              </h1>
              <p className="text-[#5D524D] mb-8">
                Enter your new password below.
              </p>

              <form onSubmit={handleConfirmReset} className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="new-password" className="text-[#2C2420]">New Password</Label>
                  <div className="relative">
                    <Input
                      id="new-password"
                      type={showPassword ? 'text' : 'password'}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="••••••••••"
                      className="caffeine-input pr-10"
                      minLength={8}
                      required
                      data-testid="new-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8C8078] hover:text-[#4A3B32]"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  <p className="text-xs text-[#8C8078]">Must be at least 8 characters</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirm-password" className="text-[#2C2420]">Confirm Password</Label>
                  <Input
                    id="confirm-password"
                    type={showPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="••••••••••"
                    className="caffeine-input"
                    required
                    data-testid="confirm-password-input"
                  />
                </div>

                <Button 
                  type="submit" 
                  className="caffeine-btn-primary w-full"
                  disabled={loading}
                  data-testid="reset-password-btn"
                >
                  {loading ? 'Updating...' : 'Update Password'}
                </Button>
              </form>
            </div>
          </div>
        </div>

        <div className="hidden lg:flex w-1/2 bg-[#4A3B32] items-center justify-center p-12">
          <div className="max-w-md text-center">
            <div className="w-20 h-20 bg-white/10 rounded-2xl flex items-center justify-center mx-auto mb-8">
              <Lock className="w-10 h-10 text-white/80" />
            </div>
            <h2 className="font-heading text-3xl text-white mb-4">
              Secure Your Account
            </h2>
            <p className="text-white/70">
              Choose a strong password to protect your account.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Email Sent View
  if (emailSent) {
    return (
      <div className="min-h-screen bg-[#F9F7F5] flex items-center justify-center px-4" data-testid="email-sent-page">
        <div className="w-full max-w-md text-center">
          <div className="w-16 h-16 bg-[#4A3B32]/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <Mail className="w-8 h-8 text-[#4A3B32]" />
          </div>
          <h1 className="font-heading text-2xl text-[#2C2420] mb-2">Check Your Email</h1>
          <p className="text-[#5D524D] mb-6">
            If an account exists for <strong>{email}</strong>, you&apos;ll receive a password reset link shortly.
          </p>
          <Button 
            onClick={() => navigate('/login')}
            className="caffeine-btn-primary"
          >
            Back to Login
          </Button>
          <p className="text-xs text-[#8C8078] mt-6">
            Did not receive the email? Check your spam folder or try again.
          </p>
        </div>
      </div>
    );
  }

  // Request Reset View
  return (
    <div className="min-h-screen bg-[#F9F7F5] flex" data-testid="forgot-password-page">
      <div className="w-full lg:w-1/2 flex flex-col px-8 py-6">
        <button 
          onClick={() => navigate('/login')}
          className="caffeine-btn-ghost inline-flex items-center gap-2 w-fit mb-8"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Login
        </button>

        <div className="flex-1 flex items-center justify-center">
          <div className="w-full max-w-md">
            <div className="flex items-center gap-2 mb-8">
              <div className="w-10 h-10 bg-[#4A3B32] rounded-md flex items-center justify-center">
                <span className="text-white font-bold">Q1</span>
              </div>
              <span className="font-heading text-2xl text-[#2C2420]">QIndex One</span>
            </div>

            <h1 className="font-heading text-3xl text-[#2C2420] mb-2">
              Forgot Password?
            </h1>
            <p className="text-[#5D524D] mb-8">
              Enter your email address and we&apos;ll send you a link to reset your password.
            </p>

            <form onSubmit={handleRequestReset} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-[#2C2420]">Email Address</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  className="caffeine-input"
                  required
                  data-testid="reset-email-input"
                />
              </div>

              <Button 
                type="submit" 
                className="caffeine-btn-primary w-full"
                disabled={loading}
                data-testid="send-reset-link-btn"
              >
                {loading ? 'Sending...' : 'Send Reset Link'}
              </Button>
            </form>
          </div>
        </div>
      </div>

      <div className="hidden lg:flex w-1/2 bg-[#4A3B32] items-center justify-center p-12">
        <div className="max-w-md text-center">
          <div className="w-20 h-20 bg-white/10 rounded-2xl flex items-center justify-center mx-auto mb-8">
            <Mail className="w-10 h-10 text-white/80" />
          </div>
          <h2 className="font-heading text-3xl text-white mb-4">
            Password Recovery
          </h2>
          <p className="text-white/70">
            We will help you get back into your account securely.
          </p>
        </div>
      </div>
    </div>
  );
}
